import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Bolt } from '../../../assets/icons';
import { LabelType, Pill } from '../../_ui';
import { NearbyChargerConnectionRow } from './NearbyChargerConnectionRow.component';
import { NearbyChargerSiteDetails } from './NearbyChargerSiteDetails.component';
import { OutboundActionLink } from './OutboundLink.component';
import { FINDER_STATUS_STYLE, getStatusLabelKey } from './statusStyles';
import { FinderResult } from './types';
import { formatPoiAddress, getChargeHubMapUrl } from './utils/poiDisplay';

interface NearbyChargerResultCardProps {
  result: FinderResult;
  rank: number;
  isActive: boolean;
  primaryColor: string;
  onSelect: (poiId: number) => void;
  cardRef: (poiId: number, element: HTMLDivElement | null) => void;
}

export const NearbyChargerResultCard = memo(
  ({ result, rank, isActive, primaryColor, onSelect, cardRef }: NearbyChargerResultCardProps) => {
    const { t } = useTranslation();
    const { poi, connections, status, maxPowerKw, distanceKm } = result;
    const statusStyle = FINDER_STATUS_STYLE[status];
    // A stable ref callback, so React doesn't detach and re-attach it on every render
    const setRef = useCallback(
      (element: HTMLDivElement | null) => cardRef(poi.ID, element),
      [cardRef, poi.ID],
    );

    return (
      // Clicking anywhere on the card selects it; the header button is the keyboard and
      // screen-reader control, so the links below are not nested inside a button
      <div
        ref={setRef}
        onClick={() => onSelect(poi.ID)}
        className={`flex flex-col gap-2 p-3 rounded-lg border cursor-pointer ${
          isActive ? 'border-brand2 bg-brand0' : 'border-grey2 bg-white hover:bg-grey0'
        }`}
        data-testid={`nearby-charger-result-${rank}`}
      >
        {/* Buttons may only contain phrasing content, so this uses spans rather than Label */}
        <button
          type='button'
          aria-pressed={isActive}
          className='flex flex-row gap-3 w-full text-left rounded outline-none focus-visible:ring-2 focus-visible:ring-brand2'
        >
          <span
            className='w-7 h-7 shrink-0 rounded-full grid place-items-center text-white text-sm font-semibold font-inter'
            style={{ backgroundColor: primaryColor }}
          >
            {rank}
          </span>
          <span className='flex flex-col gap-0.5 flex-1 min-w-0'>
            <span className='flex flex-row justify-between items-baseline gap-2'>
              <span className={`${LabelType.LABEL_S} text-black break-words min-w-0`}>
                {poi.AddressInfo?.Title || t('nearby_chargers_unnamed_station')}
              </span>
              <span className={`${LabelType.LABEL_S} text-black whitespace-nowrap`}>
                {t('nearby_chargers_distance_km', { distance: distanceKm.toFixed(1) })}
              </span>
            </span>
            <span className={`${LabelType.BODY4} text-grey5`}>{formatPoiAddress(poi)}</span>
          </span>
        </button>

        {/* Aligned with the title, past the rank badge */}
        <div className='flex flex-col gap-2 pl-10 min-w-0'>
          <div className='flex flex-row flex-wrap gap-1'>
            <Pill
              label={t(getStatusLabelKey(status))}
              bgColor={statusStyle.pillBgColor}
              labelColor={statusStyle.pillLabelColor}
            />
            {maxPowerKw > 0 && (
              <Pill label={t('nearby_chargers_up_to_kw', { power: maxPowerKw })} iconLeft={Bolt} />
            )}
          </div>

          <div className='flex flex-col gap-1 bg-grey0 rounded p-2'>
            {connections.map((connection) => (
              <NearbyChargerConnectionRow key={connection.ID} connection={connection} />
            ))}
          </div>

          <NearbyChargerSiteDetails poi={poi} />

          {/* Open Charge Map has no live availability; ChargeHub does */}
          <OutboundActionLink
            href={getChargeHubMapUrl(result)}
            label={t('nearby_chargers_check_chargehub_availability')}
          />
        </div>
      </div>
    );
  },
);

NearbyChargerResultCard.displayName = 'NearbyChargerResultCard';
